// src/data/challengeLibrary.js
// ─────────────────────────────────────────────────────────────
//  Single source of truth for the challenge template library.
//
//  Previously defined inline in GrowthHub.jsx (exported from there).
//  Extracted here so useAutoCheckIn (mounted in Layout) can import
//  it directly without dynamically loading the entire GrowthHub page.
//
//  GrowthHub re-exports this for any consumer that still imports from there.
//  useAutoCheckIn imports it directly — no dynamic import, no cache, no cycle.
// ─────────────────────────────────────────────────────────────

// ── Challenge → level task contribution map ──────────────────────────────────
//
//  `contributes` lists the milestone type IDs (from LEVEL_REQUIREMENTS in
//  useFeatureUnlock) that completing this challenge advances.
//
//  ALL challenges contribute to the challenge-count milestones
//  (challenge_completed, challenges_5, challenges_10, challenges_20) because
//  those are simply cumulative counts of any completed challenge.
//
//  ADDITIONALLY, some challenges also contribute to other milestone types:
//    • Prayer challenges   → prayer_streak_7 / prayer_streak_14 / prayer_streak_30
//    • Word challenges     → first_bible_study / reading_plan_streak_7 / _30
//    • Sermon challenges   → first_sermon_note / sermons_4 / sermons_10
//    • Reading challenges  → first_reading_plan / reading_plan_streak_7 / _30
//    • Evangelism/gospel   → first_gospel_shared / first_salvation_decision
//    • Discipleship        → first_disc_attendance / first_disciple_added
//    • Stewardship         → first_tithe / first_missionary_support
//    • Per-disciple        → disc_salvation / disc_shared_faith / disc_led_prayer / disc_led_study
//
//  This metadata is used by:
//    1. LevelProgressHero in GrowthHub — shows "🔥 Active challenge counting
//       toward this task" badges on relevant level task rows.
//    2. getLevelTaskHint (useFeatureUnlock) — can surface the best matching
//       active challenge as a progress nudge inside the hint.
//
//  NOTE: contributing to a milestone type does NOT replace the actual trigger
//  in useAutoMilestones — it is display-only metadata for the level panel.
// ─────────────────────────────────────────────────────────────────────────────

// Stores whose data shape is verified for local-day matching (date / createdAt).
// bibleReadingHistory is left out: it records only the FIRST read of each chapter, so it
// cannot prove a read on a given day (re-reads would be stripped); configs with
// filterCategory / countField are left out because their stores don't fit the check.
const TRACKED_STORE_KEYS = new Set([
  'sermonNotes', 'calendarEvents', 'dailyEntries', 'expenses',
  'discipleshipAttendance', 'prayerCheckins', 'peopleContacts', 'tithesOffering',
  'memoryVerseDays',
]);

// Resolves a challenge's real-data tracker from its library template, so challenges
// started without an `autoProgress` field are still tracked. null = no verified tracker.
export function resolveAutoProgress(challenge) {
  const tpl = CHALLENGE_LIBRARY.find(t => t.id === challenge?.templateId);
  const ap = tpl?.autoProgress ?? challenge?.autoProgress;
  if (!ap?.storeKey || ap.filterCategory || ap.countField) return null;
  return TRACKED_STORE_KEYS.has(ap.storeKey) ? ap : null;
}

// Consecutive-day challenges: progress is the current unbroken run of days.
export function isConsecutiveChallenge(challenge) {
  const tpl = CHALLENGE_LIBRARY.find(t => t.id === challenge?.templateId);
  return !!tpl?.consecutive;
}

// Shared base — every challenge counts toward challenge-count milestones.
const CHALLENGE_COUNT_MILESTONES = [
  'challenge_completed', // first completion
  'challenges_5',        // 5 total
  'challenges_10',       // 10 total
  'challenges_20',       // 20 total
];

// ── Challenge library — grouped by Sheepheard level stage ───────
//
//  CHALLENGE FIELDS:
//    id            - Unique identifier
//    stage         - Maturity stage: 'listener' | 'follower' | 'gathered' | 'bearer' | 'sheepheard'
//    days          - Duration in days
//    emoji         - Display emoji
//    label         - Display name
//    desc          - Description
//    category      - Category for auto-check-in matching
//    roles         - Role restrictions (null = all roles)
//    contributes   - Milestone types this challenge advances
//    levelTaskHint - Hint text for level tasks
//    requiresManualCheckIn - If true, challenge requires manual check-in (moved to level 5)
//    autoProgress  - Configuration for automatic progress tracking:
//                    { storeKey: string, countField?: string, threshold?: number }
//                    - storeKey: The store key to monitor for progress
//                    - countField: Field to count (if not provided, counts array length)
//                    - threshold: Minimum count to mark a day as complete
// ─────────────────────────────────────────────────────────────────────────────

export const CHALLENGE_LIBRARY = [

  // ═══════════════════════════════════════════════════════════════
  //  LISTENER — Level 1 → 2 requirements
  //  "My sheep hear My voice" (John 10:27a)
  //  Covers: first_sermon_note · sermons_4 · prayer_streak_7
  //          first_reading_plan · first_bible_study · challenge_completed
  // ═══════════════════════════════════════════════════════════════

  {
    id: 'pray_3_daily', autoProgress: { storeKey: 'prayerCheckins', threshold: 3 },      stage: 'listener',   days: 7,  emoji: '🙏',
    label: 'Pray for 3 People Daily',
    desc: 'Every day for 7 days, pray for 3 specific people by name.',
    category: 'prayer',      roles: null,
    contributes: [...CHALLENGE_COUNT_MILESTONES, 'prayer_streak_7', 'prayer_streak_14', 'prayer_streak_30'],
    levelTaskHint: 'Each check-in logs a prayer day — counts directly toward Prayer Streak tasks.',
  },
  {
    id: 'fast_pray',         stage: 'listener',   days: 7,  emoji: '🕊️',
    label: '7-Day Prayer & Fasting',
    desc: 'Set aside one meal per day to pray. Use meal times as dedicated prayer time.',
    category: 'prayer',      roles: null,
    contributes: [...CHALLENGE_COUNT_MILESTONES, 'prayer_streak_7', 'prayer_streak_14', 'prayer_streak_30'],
    levelTaskHint: 'Each check-in logs a prayer day — counts directly toward Prayer Streak tasks.',
  },
  {
    id: 'sermon_notes_starter', stage: 'listener', days: 21, emoji: '📝',
    label: '21-Day Sermon Journaling',
    desc: 'Every time you hear a sermon, open Sermon Notes and record at least a title, speaker, or one key insight. Check in on each day you save a note.',
    category: 'word',        roles: null,
    contributes: [
      ...CHALLENGE_COUNT_MILESTONES,
      'first_sermon_note',
      'sermons_4',
      'daily_faithful_30',
      'daily_faithful_60',
    ],
    levelTaskHint: 'Directly satisfies "Save Your First Sermon Note" and builds toward the "4 Sermons Noted" Level 1 → 2 tasks.',
    autoProgress: { storeKey: 'sermonNotes', threshold: 1 },
  },
  {
    id: 'reading_plan_starter', stage: 'listener', days: 14, emoji: '📖',
    label: '14-Day Reading Plan Kickstart',
    desc: 'Open the Bible page, start a reading plan, and read at least one chapter every day for 14 days. Check in each day you read.',
    category: 'word',        roles: null,
    contributes: [
      ...CHALLENGE_COUNT_MILESTONES,
      'first_reading_plan',
      'reading_plan_streak_7',
      'first_bible_study',
      'daily_faithful_30',
      'daily_faithful_60',
    ],
    levelTaskHint: 'Starting a plan satisfies "Start a Reading Plan". 14 days of daily reading builds your 7-Day Reading Streak — both Level 1 → 2 tasks.',
    autoProgress: { storeKey: 'bibleReadingHistory', threshold: 1 },
  },

  // ═══════════════════════════════════════════════════════════════
  //  FOLLOWER — Level 2 → 3 requirements
  //  "They follow Me" (John 10:27c)
  //  Covers: first_gospel_shared · reading_plan_streak_7 · prayer_streak_14
  //          first_disc_attendance · sermons_10 · challenges_5
  // ═══════════════════════════════════════════════════════════════

  {
    id: 'bible_chapter',     stage: 'follower',   days: 21, emoji: '📖',
    label: '21-Day Chapter-a-Day',
    desc: 'Read one Bible chapter every day for 21 days and journal one insight.',
    category: 'word',        roles: null,
    contributes: [
      ...CHALLENGE_COUNT_MILESTONES,
      'first_bible_study',
      'reading_plan_streak_7',
      'reading_plan_streak_30',
    ],
    levelTaskHint: 'Builds your Reading Plan streak and contributes toward Bible Study tasks.',
    autoProgress: { storeKey: 'bibleReadingHistory', threshold: 1 },
  },
  {
    id: 'memory_verse',      stage: 'follower',   days: 30, emoji: '💬',
    label: '30-Day Scripture Memory',
    desc: 'Memorize one verse per week for 30 days. Review all previous verses each day.',
    category: 'word',        roles: null,
    contributes: [
      ...CHALLENGE_COUNT_MILESTONES,
      'first_bible_study',
      'reading_plan_streak_7',
      'reading_plan_streak_30',
      'daily_faithful_30',
      'daily_faithful_60',
    ],
    levelTaskHint: '30 days of daily Word engagement — strong contributor to Faithful (30 & 60-day) & Reading tasks.',
    autoProgress: { storeKey: 'memoryVerseDays', threshold: 1 },
  },
  {
    id: 'sermon_notes_ten',  stage: 'follower',   days: 60, emoji: '📖',
    label: '10 Sermons Documented',
    desc: 'Over 60 days, save a sermon note every time you hear a message preached — title, speaker, scripture, or one insight. Check in each day you record a note.',
    category: 'word',        roles: null,
    contributes: [
      ...CHALLENGE_COUNT_MILESTONES,
      'first_sermon_note',
      'sermons_4',
      'sermons_10',
      'daily_faithful_30',
      'daily_faithful_60',
    ],
    levelTaskHint: '60 days covers 8–10 Sundays — builds toward both the "4 Sermons" and "10 Sermons Noted" Level 2 → 3 tasks.',
    autoProgress: { storeKey: 'sermonNotes', threshold: 1 },
  },
  {
    id: 'disc_attendance_starter', autoProgress: { storeKey: 'discipleshipAttendance', threshold: 1 }, stage: 'follower', days: 21, emoji: '🤝',
    label: '21-Day Discipleship Commitment',
    desc: 'Over 21 days, attend or participate in at least one discipleship meeting, Bible group, or one-on-one mentoring session. Log each attendance day in your Journal or mark it on the Calendar. Check in on days you meet or prepare together.',
    category: 'discipleship', roles: null,
    contributes: [
      ...CHALLENGE_COUNT_MILESTONES,
      'first_disc_attendance',
      'daily_faithful_30',
      'daily_faithful_60',
    ],
    levelTaskHint: 'Log each discipleship meeting day in your Journal or Calendar — this directly satisfies the "First Discipleship Meeting" Level 2 → 3 task. Discipleship Keeper is available from Level 1.',
  },

  // ═══════════════════════════════════════════════════════════════
  //  GATHERED — Level 3 → 4 requirements
  //  "I know them" (John 10:27b)
  //  Covers: first_disciple_added · daily_faithful_30 · reading_plan_streak_30
  //          prayer_streak_30 · first_salvation_decision · challenges_10
  //          first_missionary_support
  // ═══════════════════════════════════════════════════════════════

  {
    id: 'invite_someone',    stage: 'gathered',   days: 7,  emoji: '👋',
    label: 'Invite Someone Every Day',
    desc: 'Invite at least one person to church or small group every day for 7 days.',
    category: 'evangelism',  roles: null,
    contributes: [...CHALLENGE_COUNT_MILESTONES, 'first_gospel_shared'],
    levelTaskHint: 'Inviting others opens gospel conversations — contributes toward Gospel Shared task.',
  },
  {
    id: 'share_gospel',      stage: 'gathered',   days: 7,  emoji: '🗣️',
    label: '7-Day Gospel Conversation',
    desc: 'Have at least one meaningful gospel conversation every day for a week.',
    category: 'evangelism',  roles: null,
    contributes: [...CHALLENGE_COUNT_MILESTONES, 'first_gospel_shared'],
    levelTaskHint: 'Direct gospel sharing — counts toward the "Share the Gospel" level task.',
  },
  {
    id: 'campus_outreach',   stage: 'gathered',   days: 7,  emoji: '🎓',
    label: '7-Day Campus Outreach',
    desc: 'Pray for your campus and talk to one classmate about faith every day.',
    category: 'evangelism',  roles: ['student'],
    contributes: [...CHALLENGE_COUNT_MILESTONES, 'first_gospel_shared', 'prayer_streak_7'],
    levelTaskHint: 'Daily outreach + prayer — counts toward Gospel Shared and Prayer Streak tasks.',
  },
  {
    id: 'add_first_disciple', autoProgress: { storeKey: 'prayerCheckins', threshold: 1 }, stage: 'gathered',  days: 14, emoji: '🌱',
    label: 'Add Your First Disciple',
    desc: 'Over 14 days, pray daily for God to show you who to disciple. When He does, add them in the Discipleship Keeper. Check in each day you pray or take a step toward this.',
    category: 'discipleship', roles: null,
    contributes: [
      ...CHALLENGE_COUNT_MILESTONES,
      'first_disciple_added',
      'prayer_streak_7',
      'prayer_streak_14',
      'first_disc_attendance',
    ],
    levelTaskHint: 'Adding a disciple in Discipleship Keeper directly satisfies the "Add Your First Disciple" Level 3 → 4 task.',
  },
  {
    id: 'evangelism_decision', stage: 'gathered', days: 30, emoji: '🕊️',
    label: '30-Day Evangelism Push',
    desc: 'For 30 days, pray daily for someone who does not yet know Christ, share the gospel actively, and trust God for a salvation decision. Record contacts and decisions in People Keeper.',
    category: 'evangelism',  roles: null,
    contributes: [
      ...CHALLENGE_COUNT_MILESTONES,
      'first_gospel_shared',
      'first_salvation_decision',
      'prayer_streak_14',
      'prayer_streak_30',
      'daily_faithful_30',
      'daily_faithful_60',
    ],
    levelTaskHint: 'Daily prayer + active sharing — contributes toward Gospel Shared, Lead to Salvation, Prayer Streak (14 & 30-day), and Faithfulness tasks.',
  },
  {
    id: 'support_missionary', autoProgress: { storeKey: 'prayerCheckins', threshold: 1 }, stage: 'gathered',  days: 7,  emoji: '🌍',
    label: 'Support a Missionary',
    desc: 'This week, research a missionary or ministry your church supports. Pray for them daily, then record your first missionary support in the Stewardship ledger.',
    category: 'stewardship', roles: null,
    contributes: [
      ...CHALLENGE_COUNT_MILESTONES,
      'first_missionary_support',
      'prayer_streak_7',
    ],
    levelTaskHint: 'Recording missionary support in the Stewardship ledger directly satisfies the "First Missionary Supported" Level 3 → 4 task.',
  },

  // ═══════════════════════════════════════════════════════════════
  //  BEARER — Level 4 → 5 requirements
  //  "I give them eternal life" (John 10:28)
  //  Covers: first_tithe · challenges_20 · daily_faithful_60
  //          disc_salvation · disc_shared_faith · disc_led_prayer · disc_led_study
  // ═══════════════════════════════════════════════════════════════

  {
    id: 'workplace_pray',    stage: 'bearer',     days: 30, emoji: '💼',
    label: '30-Day Workplace Prayer',
    desc: "Pray for one coworker by name every day and look for an opportunity to show Christ's love.",
    category: 'prayer',      roles: ['professional'],
    contributes: [
      ...CHALLENGE_COUNT_MILESTONES,
      'prayer_streak_14',
      'prayer_streak_30',
      'daily_faithful_30',
      'daily_faithful_60',
    ],
    levelTaskHint: '30 days of prayer — strong contributor to Prayer Streak (14 & 30-day) and Faithfulness (30 & 60-day) tasks.',
  },
  {
    id: 'tithe_commitment', autoProgress: { storeKey: 'prayerCheckins', threshold: 1 },  stage: 'bearer',     days: 14, emoji: '💰',
    label: 'Commit to Tithing',
    desc: 'Over 14 days, pray daily about faithful giving. Calculate your tithe, plan your gift, then record your first tithe in the Stewardship ledger. Check in each day you pray or give.',
    category: 'stewardship', roles: null,
    contributes: [
      ...CHALLENGE_COUNT_MILESTONES,
      'first_tithe',
      'prayer_streak_7',
      'daily_faithful_30',
      'daily_faithful_60',
    ],
    levelTaskHint: 'Recording your tithe in the Stewardship ledger directly satisfies the "First Tithe Recorded" Level 4 → 5 task.',
  },
  {
    id: 'disciple_to_salvation', autoProgress: { storeKey: 'prayerCheckins', requestCategory: 'timothy', threshold: 1 }, stage: 'bearer', days: 30, emoji: '✝️',
    label: 'Walk a Disciple to Salvation',
    desc: 'For 30 days, intentionally walk a disciple toward faith — pray for them daily, share the gospel together, and trust God for a decision. Record their salvation milestone in DiscipleshipKeeper.',
    category: 'discipleship', roles: null,
    contributes: [
      ...CHALLENGE_COUNT_MILESTONES,
      'disc_salvation',
      'disc_shared_faith',
      'first_salvation_decision',
      'prayer_streak_14',
      'prayer_streak_30',
      'daily_faithful_30',
      'daily_faithful_60',
    ],
    levelTaskHint: 'Recording your disciple\'s salvation in DiscipleshipKeeper satisfies the "Disciple Receives Salvation" and "Disciple Shares Faith" Level 4 → 5 tasks.',
  },
  {
    id: 'disciple_leads_out', stage: 'bearer',    days: 21, emoji: '🎤',
    label: 'Help a Disciple Lead',
    desc: 'Over 21 days, mentor a disciple toward spiritual leadership. Invite them to lead a prayer or facilitate a Bible study. Check in each day you invest in their growth. Record it in DiscipleshipKeeper.',
    category: 'discipleship', roles: null,
    contributes: [
      ...CHALLENGE_COUNT_MILESTONES,
      'disc_led_prayer',
      'disc_led_study',
      'first_disc_attendance',
      'daily_faithful_30',
      'daily_faithful_60',
    ],
    levelTaskHint: 'Recording your disciple leading prayer or a study in DiscipleshipKeeper satisfies the "Disciple Leads Prayer/Study" Level 4 → 5 tasks.',
  },

  // ═══════════════════════════════════════════════════════════════
  //  SHEEPHEARD — multiplication and legacy (Level 5)
  //  "One flock, one shepherd" (John 10:16)
  //  NOTE: Challenges that require manual check-in are placed at this level
  //  because they require mature self-discipline and cannot be auto-tracked.
  // ═══════════════════════════════════════════════════════════════

  // ── Manual check-in challenges (require self-discipline) ───────────────────
  {
    id: 'disciple_contact',  stage: 'sheepheard', days: 7,  emoji: '🌳',
    label: '7-Day Disciple Check-in',
    desc: 'Contact each of your disciples at least once this week to encourage and pray with them.',
    category: 'mission',     roles: ['planter','pastor','elder','widowedPastor'],
    contributes: [
      ...CHALLENGE_COUNT_MILESTONES,
      'first_disc_attendance',
    ],
    levelTaskHint: 'Regular disciple check-ins count toward the Discipleship Attendance level task.',
  },
  {
    id: 'sermon_prep',       stage: 'sheepheard', days: 21, emoji: '📝',
    label: '21-Day Daily Study',
    desc: 'Spend at least 20 minutes in deep Bible study for sermon or teaching preparation daily.',
    category: 'word',        roles: ['pastor','widowedPastor'],
    contributes: [
      ...CHALLENGE_COUNT_MILESTONES,
      'first_bible_study',
      'reading_plan_streak_7',
      'reading_plan_streak_30',
      'daily_faithful_30',
      'daily_faithful_60',
    ],
    levelTaskHint: '21 days of deep study — strong contributor to Bible Study, Reading Streak, and Faithfulness tasks.',
  },

  // ═══════════════════════════════════════════════════════════════
  //  STEWARDSHIP & FINANCIAL DISCIPLESHIP
  //  Covers: budget tracking, tithes, missionary support, expense management
  // ═══════════════════════════════════════════════════════════════

  {
    id: 'budget_tracking',   stage: 'follower',   days: 14, emoji: '💰',
    label: '14-Day Budget Tracking',
    desc: 'Track every expense for 14 consecutive days. Record each purchase in the Stewardship ledger.',
    category: 'stewardship', roles: null,
    contributes: [...CHALLENGE_COUNT_MILESTONES],
    levelTaskHint: 'Consistent expense tracking builds financial discipline.',
    autoProgress: { storeKey: 'expenses', threshold: 1 },
  },
  {
    id: 'tithe_consistency', stage: 'gathered',   days: 4, emoji: '🙏',
    label: 'Tithe Consistency',
    desc: 'Record a tithe or offering on 4 different days. Use the Tithes & Offering section.',
    category: 'stewardship', roles: null,
    contributes: [
      ...CHALLENGE_COUNT_MILESTONES,
      'first_tithe',
    ],
    levelTaskHint: 'Weekly tithing builds the habit of faithful giving.',
    // Tracked from the dedicated tithes store: one check-in per day that has a tithe record.
    autoProgress: { storeKey: 'tithesOffering', threshold: 1 },
  },
  // (duplicate of support_missionary removed)
  {
    id: 'expense_audit',     stage: 'bearer',     days: 7,  emoji: '📊',
    label: 'Weekly Expense Audit',
    desc: 'At the end of each week for 7 days, review your expenses and categorize them. Identify one area to reduce spending.',
    category: 'stewardship', roles: null,
    contributes: [...CHALLENGE_COUNT_MILESTONES],
    levelTaskHint: 'Regular expense review builds financial wisdom.',
  },
  // ═══════════════════════════════════════════════════════════════
  //  BIBLE STUDY & DISCIPLESHIP
  //  Covers: Bible study completion, discipleship milestones
  // ═══════════════════════════════════════════════════════════════

  {
    id: 'bible_study_completion', stage: 'sheepheard', days: 21, emoji: '📖',
    label: 'Complete 3 Bible Studies',
    desc: 'Over 21 days, complete three different Bible study guides. Mark each completion in your study progress.',
    category: 'discipleship', roles: null,
    contributes: [
      ...CHALLENGE_COUNT_MILESTONES,
      'first_bible_study',
    ],
    levelTaskHint: 'Completing Bible studies builds spiritual maturity.',
    autoProgress: { storeKey: 'bibleStudyProgress', countField: 'completed', threshold: 1 },
  },
  {
    id: 'disciple_meeting',  stage: 'gathered',   days: 14, emoji: '🤝',
    label: '14-Day Discipleship Meetings',
    desc: 'Attend or lead a discipleship meeting every other day for 14 days. Record each meeting in your attendance log.',
    category: 'discipleship', roles: null,
    contributes: [
      ...CHALLENGE_COUNT_MILESTONES,
      'first_disc_attendance',
    ],
    levelTaskHint: 'Regular discipleship meetings build community.',
    autoProgress: { storeKey: 'discipleshipAttendance', threshold: 1 },
  },
  {
    id: 'disciple_prayer',   stage: 'bearer',     days: 30, emoji: '🙏',
    label: 'Pray for Disciples Daily',
    desc: 'Pray for each of your disciples by name every day for 30 days. Mark each day in your prayer journal.',
    category: 'prayer',      roles: null,
    contributes: [
      ...CHALLENGE_COUNT_MILESTONES,
      'prayer_streak_14',
      'prayer_streak_30',
      'daily_faithful_30',
      'daily_faithful_60',
    ],
    levelTaskHint: 'Praying for disciples builds spiritual leadership.',
    autoProgress: { storeKey: 'prayerCheckins', requestCategory: 'timothy', threshold: 1 },
  },
  {
    id: 'disciple_baptism',  stage: 'sheepheard', days: 60, emoji: '💧',
    label: 'Lead Disciple to Baptism',
    desc: 'Over 60 days, disciple someone toward baptism. Record key milestones in their spiritual journey.',
    category: 'discipleship', roles: null,
    contributes: [
      ...CHALLENGE_COUNT_MILESTONES,
      'disc_salvation',
    ],
    levelTaskHint: 'Leading to baptism is the ultimate discipleship goal.',
  },

  // ═══════════════════════════════════════════════════════════════
  //  CALENDAR & MEETING MANAGEMENT
  //  Covers: event creation, meeting attendance, calendar organization
  // ═══════════════════════════════════════════════════════════════

  {
    id: 'calendar_setup',    stage: 'listener',   days: 7,  emoji: '📅',
    label: '7-Day Calendar Setup',
    desc: 'Create at least one meaningful event in your calendar each day for 7 days. Include church events, meetings, or personal devotion times.',
    category: 'calendar',    roles: null,
    contributes: [...CHALLENGE_COUNT_MILESTONES],
    levelTaskHint: 'Using the calendar builds intentional discipleship.',
    autoProgress: { storeKey: 'calendarEvents', threshold: 1 },
  },
  {
    id: 'meeting_attendance', stage: 'follower',   days: 21, emoji: '✅',
    label: '21-Day Meeting Attendance',
    desc: 'Attend church services or small group meetings for 21 out of the next 21 days. Mark each attendance in your calendar.',
    category: 'calendar',    roles: null,
    contributes: [
      ...CHALLENGE_COUNT_MILESTONES,
      'first_disc_attendance',
    ],
    levelTaskHint: 'Consistent attendance builds community.',
    autoProgress: { storeKey: 'calendarEvents', threshold: 1 },
  },
  {
    id: 'event_planning',    stage: 'gathered',   days: 14, emoji: '🎯',
    label: 'Plan 3 Events',
    desc: 'Plan and schedule three meaningful events over 14 days. These could be outreach events, discipleship meetings, or service projects.',
    category: 'calendar',    roles: null,
    contributes: [...CHALLENGE_COUNT_MILESTONES],
    levelTaskHint: 'Event planning develops leadership skills.',
    autoProgress: { storeKey: 'calendarEvents', threshold: 1 },
  },
  {
    id: 'weekly_review',     stage: 'bearer',     days: 30, emoji: '🔄',
    label: 'Monthly Calendar Review',
    desc: 'At the end of each week for 30 days, review your calendar and plan the next week. Identify areas for improvement.',
    category: 'calendar',    roles: null,
    contributes: [...CHALLENGE_COUNT_MILESTONES],
    levelTaskHint: 'Regular review builds time management skills.',
  },
  {
    id: 'disciple_scheduling', stage: 'sheepheard', days: 21, emoji: '⏰',
    label: 'Schedule Discipleship Meetings',
    desc: 'Schedule and attend discipleship meetings with each of your disciples over 21 days. Use the calendar to track these commitments.',
    category: 'calendar',    roles: null,
    contributes: [
      ...CHALLENGE_COUNT_MILESTONES,
      'first_disc_attendance',
    ],
    levelTaskHint: 'Scheduling discipleship meetings builds accountability.',
    autoProgress: { storeKey: 'calendarEvents', threshold: 1 },
  },

  // ═══════════════════════════════════════════════════════════════
  //  EVANGELISM & MISSIONS
  //  Covers: gospel sharing, outreach, mission support
  // ═══════════════════════════════════════════════════════════════

  {
    id: 'daily_witness',     stage: 'follower',   days: 30, emoji: '🗣️',
    label: '30-Day Daily Witness',
    desc: 'Share your faith or do a good deed for someone every day for 30 days. Record each encounter in your People Keeper.',
    category: 'evangelism',  roles: null,
    contributes: [
      ...CHALLENGE_COUNT_MILESTONES,
      'first_gospel_shared',
      'prayer_streak_14',
      'prayer_streak_30',
      'daily_faithful_30',
      'daily_faithful_60',
    ],
    levelTaskHint: 'Daily witness builds evangelistic character.',
    autoProgress: { storeKey: 'peopleContacts', threshold: 1 },
  },
  {
    id: 'mission_prayer',    stage: 'gathered',   days: 14, emoji: '🌍',
    label: '14-Day Mission Prayer',
    desc: 'Pray for a different mission field or missionary each day for 14 days. Record your prayers in your prayer journal.',
    category: 'evangelism',  roles: null,
    contributes: [
      ...CHALLENGE_COUNT_MILESTONES,
      'prayer_streak_7',
      'prayer_streak_14',
    ],
    levelTaskHint: 'Mission prayer builds global vision.',
    autoProgress: { storeKey: 'prayerCheckins', threshold: 1 },
  },
  {
    id: 'outreach_event',    stage: 'bearer',     days: 21, emoji: '🤝',
    label: 'Plan Outreach Event',
    desc: 'Plan and execute an outreach event over 21 days. Invite friends, prepare materials, and follow up with attendees.',
    category: 'evangelism',  roles: null,
    contributes: [
      ...CHALLENGE_COUNT_MILESTONES,
      'first_gospel_shared',
    ],
    levelTaskHint: 'Outreach events develop missional leadership.',
  },
  {
    id: 'disciple_evangelism', stage: 'sheepheard', days: 40, emoji: '✝️',
    label: 'Train Disciple in Evangelism',
    desc: 'Over 40 days, train one of your disciples in evangelism. Model, teach, and practice together.',
    category: 'evangelism',  roles: null,
    contributes: [
      ...CHALLENGE_COUNT_MILESTONES,
      'disc_shared_faith',
    ],
    levelTaskHint: 'Training disciples in evangelism multiplies impact.',
  },

  // ═══════════════════════════════════════════════════════════════
  //  SPIRITUAL DISCIPLINES
  //  Covers: fasting, silence, solitude, worship
  // ═══════════════════════════════════════════════════════════════

  {
    id: 'fasting_discipline', stage: 'listener',   days: 7,  emoji: '🍽️',
    label: '7-Day Fasting Discipline',
    desc: 'Fast from one meal each day for 7 days, using that time for prayer and Bible reading.',
    category: 'spiritual',    roles: null,
    contributes: [
      ...CHALLENGE_COUNT_MILESTONES,
      'prayer_streak_7',
    ],
    levelTaskHint: 'Fasting builds spiritual focus.',
  },
  {
    id: 'silence_solitude',  stage: 'follower',   days: 14, emoji: '🤫',
    label: '14-Day Silence & Solitude',
    desc: 'Spend 15 minutes in silence and solitude each day for 14 days. Use this time for reflection and listening to God.',
    category: 'spiritual',    roles: null,
    contributes: [
      ...CHALLENGE_COUNT_MILESTONES,
      'daily_faithful_30',
    ],
    levelTaskHint: 'Silence and solitude build spiritual sensitivity.',
  },
  {
    id: 'scripture_meditation', stage: 'bearer',   days: 21, emoji: '💭',
    label: '21-Day Scripture Meditation',
    desc: 'Meditate on one verse or passage each day for 21 days. Write down insights and how it applies to your life.',
    category: 'spiritual',    roles: null,
    contributes: [
      ...CHALLENGE_COUNT_MILESTONES,
      'first_bible_study',
      'reading_plan_streak_7',
    ],
    levelTaskHint: 'Scripture meditation builds spiritual depth.',
  },

  // ═══════════════════════════════════════════════════════════════
  //  NEW CHALLENGES FOR LEVEL-BASED PROGRESSION
  //  (Level 0-5 challenge distribution)
  // ═══════════════════════════════════════════════════════════════

  // Level 0 (Seeker) - Listener Stage
  {
    id: 'journal_habit',    stage: 'listener',   days: 7,  emoji: '📓',
    label: '7-Day Journal Habit',
    desc: 'Write in your journal every day for 7 days. Record your thoughts, prayers, or reflections.',
    category: 'practice',   roles: null,
    contributes: [...CHALLENGE_COUNT_MILESTONES, 'daily_faithful_30', 'daily_faithful_60'],
    levelTaskHint: 'Daily journaling builds the habit of spiritual reflection.',
    autoProgress: { storeKey: 'dailyEntries', threshold: 1 },
  },

  // Level 1 (Listener) - Listener Stage
  {
    id: 'daily_devotional',  stage: 'listener',   days: 14, emoji: '📔',
    label: '14-Day Daily Devotional',
    consecutive: true,
    desc: 'Complete a daily devotional entry — passage, principle, and practice — every day for 14 days in a row. If you miss a day, open that day in your journal and write in it to keep the streak.',
    category: 'word',        roles: null,
    contributes: [
      ...CHALLENGE_COUNT_MILESTONES,
      'daily_faithful_30',
      'daily_faithful_60',
    ],
    levelTaskHint: 'Daily devotional entries build consistent time in the Word — the Level 1 challenge referenced by useFeatureUnlock.js.',
    autoProgress: { storeKey: 'dailyEntries', threshold: 1 },
  },

  // Level 2 (Follower) - Follower Stage
  {
    id: 'prayer_walk',       stage: 'follower',  days: 14, emoji: '🚶',
    label: '14-Day Prayer Walk',
    desc: 'Take a daily walk while praying. Pray for your neighborhood, community, or specific intentions.',
    category: 'prayer',      roles: null,
    contributes: [
      ...CHALLENGE_COUNT_MILESTONES,
      'prayer_streak_7',
      'prayer_streak_14',
      'daily_faithful_30',
    ],
    levelTaskHint: 'Combines physical activity with prayer — builds consistency.',
  },

  // Level 3 (Gathered) - Gathered Stage
  {
    id: 'prayer_streak', autoProgress: { storeKey: 'prayerCheckins', threshold: 1 },     stage: 'gathered',  days: 14, emoji: '📈',
    label: '14-Day Prayer Consistency',
    desc: 'Pray at least once every day for 14 consecutive days. Log each day in your prayer journal.',
    category: 'prayer',      roles: null,
    contributes: [
      ...CHALLENGE_COUNT_MILESTONES,
      'prayer_streak_7',
      'prayer_streak_14',
      'daily_faithful_30',
      'daily_faithful_60',
    ],
    levelTaskHint: 'Builds sustained prayer discipline — directly contributes to prayer streak milestones.',
  },

  // (duplicate of disciple_prayer removed)

  // Level 5 (Sheepheard) - Sheepheard Stage
  {
    id: 'disciple_multiplication', stage: 'sheepheard', days: 30, emoji: '🌱',
    label: '30-Day Disciple Multiplication',
    desc: 'Train one of your disciples to make disciples. Coach them through leading someone else to faith.',
    category: 'discipleship', roles: null,
    contributes: [
      ...CHALLENGE_COUNT_MILESTONES,
      'disc_shared_faith',
      'disc_led_prayer',
      'disc_led_study',
    ],
    levelTaskHint: 'Multiplication is the ultimate discipleship goal — train your disciple to make disciples.',
  },
  {
    id: 'legacy_journal',    stage: 'sheepheard', days: 21, emoji: '📖',
    label: '21-Day Legacy Journal',
    desc: 'Document your spiritual journey, lessons learned, and wisdom to pass on to the next generation.',
    category: 'practice',   roles: null,
    contributes: [...CHALLENGE_COUNT_MILESTONES],
    levelTaskHint: 'Recording your journey creates a legacy for future generations.',
  },
  {
    id: 'leadership_development', stage: 'sheepheard', days: 30, emoji: '🎯',
    label: '30-Day Leadership Development',
    desc: 'Identify and coach emerging leaders in your church or small group. Meet with them regularly.',
    category: 'leadership', roles: null,
    contributes: [
      ...CHALLENGE_COUNT_MILESTONES,
      'disc_led_prayer',
      'disc_led_study',
    ],
    levelTaskHint: 'Developing leaders multiplies your ministry impact.',
  },
];